/**
 * Batched, deferred uploads - the ONE batcher every AI feature uploads through.
 *
 * ## Why
 *
 * Creating a matter/review/playbook/table/data source used to be a single
 * multipart POST carrying the metadata AND every document. On the EU edge that
 * request is reset mid-body - the browser reports `ERR_HTTP2_PROTOCOL_ERROR`
 * and the user gets nothing: no entity, no files, no explanation.
 *
 * The fix is not chunking. It is to stop sending one enormous body: create the
 * entity from metadata alone, then push the documents up in small SEQUENTIAL
 * batches through a post-create add-files endpoint, then trigger AI processing
 * as its own call. Each request is small enough that no edge has an opinion
 * about it, and a failure lands on ONE batch instead of the whole submission.
 *
 * Tables shipped this first (#2230); this module is that logic lifted out so
 * there is one batcher rather than six.
 *
 * ## Bounds
 *
 * 20 files AND 20MB per request. Tables ran at 40/40MB successfully, but the
 * EU edge's actual body limit is unknown - half of a bound we know works is
 * the margin we buy until it is measured. A file bigger than the byte bound
 * still goes alone in its own batch: the bound is on the AGGREGATE, and a
 * single large file cannot be split without chunking, which we are not doing.
 * (See `utils/uploadLimits` for the per-file ceiling that DOES refuse a file.)
 *
 * ## Skip reasons
 *
 * The add-files endpoints answer with a standard shape:
 *
 * ```json
 * {"uploaded_count":N,"skipped_count":N,"attempted_count":N,
 *  "skipped":[{"file":"x.exe","file_name":"x.exe","reason":"..."}]}
 * ```
 *
 * and return HTTP 400 ONLY when every file in that batch was rejected - the
 * 400 body still carries `skipped`. So a 400 is a *content* verdict, not a
 * transport failure: the run records the reasons and carries on to the next
 * batch.
 *
 * ## What a transport failure costs
 *
 * Anything else (a reset, a 5xx, a timeout) is retried - three attempts, ~1s/2s/4s
 * with jitter - and then it is up to the caller what it means for the rest of
 * the run, via `onBatchError`:
 *
 * - `'stop'` (default) ends the run and reports every remaining file as failed.
 *   Correct when the batches are DEPENDENT: a playbook threads an `upload_id`
 *   through them, so batch 4 without batch 3 is not a smaller playbook.
 * - `'continue'` fails just that batch and keeps going. Correct when they are
 *   INDEPENDENT, which is the tables case - and the reason this option exists.
 *   A folder of 180 documents is nine batches of unrelated rows, and one 500 on
 *   the first of them used to cost the other 160 uploads outright.
 *
 * Either way the files that never went up are named in `failed` rather than
 * quietly dropped, and `describeUploadResult` turns that into a sentence.
 */

// ─── Bounds ──────────────────────────────────────────────────────────────────

export const UPLOAD_MAX_FILES_PER_BATCH = 20;
export const UPLOAD_MAX_BYTES_PER_BATCH = 20 * 1024 * 1024; // 20MB per request

export interface BatchBounds {
  maxFilesPerBatch?: number;
  maxBytesPerBatch?: number;
}

/**
 * Split files into batches bounded by both file count and cumulative bytes.
 *
 * A single file over the byte bound becomes its own one-file batch rather than
 * being dropped - bounding the aggregate is the point, not policing the file.
 */
export function buildUploadBatches(files: File[], bounds: BatchBounds = {}): File[][] {
  const maxFiles = bounds.maxFilesPerBatch ?? UPLOAD_MAX_FILES_PER_BATCH;
  const maxBytes = bounds.maxBytesPerBatch ?? UPLOAD_MAX_BYTES_PER_BATCH;

  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;

  for (const file of files) {
    const wouldExceed =
      current.length >= maxFiles ||
      (current.length > 0 && currentBytes + file.size > maxBytes);
    if (wouldExceed) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

// ─── Skip reasons ────────────────────────────────────────────────────────────

/**
 * One file the server refused, in the shape every caller reads.
 *
 * `file` and `file_name` are both populated because the endpoints disagree —
 * Tables' own `/documents` answers with `file`, the newer add-files endpoints
 * send both. Normalizing once here means no component ever has to guess.
 */
export interface SkippedFile {
  file: string;
  file_name: string;
  reason: string;
}

/** Read `skipped[]` out of a response (or an error body) in either casing. */
export function normalizeSkipped(payload: any): SkippedFile[] {
  const raw = payload?.skipped ?? payload?.data?.skipped;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry: any): SkippedFile | null => {
      if (typeof entry === 'string') {
        return entry.trim() ? { file: entry.trim(), file_name: entry.trim(), reason: '' } : null;
      }
      if (!entry || typeof entry !== 'object') return null;
      const name = String(entry.file_name ?? entry.file ?? entry.filename ?? entry.name ?? '').trim();
      if (!name) return null;
      const reason = String(entry.reason ?? entry.error ?? entry.message ?? '').trim();
      return { file: name, file_name: name, reason };
    })
    .filter((s): s is SkippedFile => s !== null);
}

/** How many files a batch response says actually landed. */
function readUploadedCount(payload: any, batchSize: number, skippedInBatch: number): number {
  const explicit = payload?.uploaded_count ?? payload?.uploadedCount;
  if (typeof explicit === 'number' && Number.isFinite(explicit)) return explicit;
  const uploaded = payload?.uploaded ?? payload?.documents ?? payload?.files;
  if (Array.isArray(uploaded)) return uploaded.length;
  return Math.max(0, batchSize - skippedInBatch);
}

/** True when an axios error is the "every file in this batch was rejected" 400. */
function isAllRejected(error: any): boolean {
  return error?.response?.status === 400 && normalizeSkipped(error.response.data).length > 0;
}

// ─── Retry ───────────────────────────────────────────────────────────────────

export const UPLOAD_RETRY_ATTEMPTS = 3;

/**
 * Is this worth trying again?
 *
 * YES for a server that fell over (5xx), a server asking us to slow down (429),
 * and a request that never got an answer at all - the EU edge resetting a body
 * mid-flight is exactly that, and it is the failure this whole module exists
 * for. Retrying it is free of side effects only because the backend makes
 * `/documents` idempotent per batch key; see `batchKey` on the send context.
 *
 * NO for anything the server decided on purpose. A 400/401/403/409 is an
 * answer, and asking the same question three times does not change it - it
 * just triples the delay before the user is told.
 */
function isRetryable(error: any): boolean {
  const status = error?.response?.status;
  if (typeof status === 'number') {
    if (status === 429) return true;
    return status >= 500;
  }
  // No response: a reset, a timeout, a dropped connection. `ERR_CANCELED` is
  // the caller aborting on purpose, which is not a failure to retry.
  return error?.code !== 'ERR_CANCELED';
}

/** Exponential backoff with jitter: ~1s, ~2s, ~4s. */
function retryDelayMs(attempt: number): number {
  const base = 1000 * 2 ** (attempt - 1);
  // Full jitter. A folder of 180 files is nine batches that all failed at the
  // same instant; retrying them on the same schedule re-creates the thundering
  // herd that knocked the server over.
  return base / 2 + Math.random() * (base / 2);
}

const sleep = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

// ─── Sequential runner ───────────────────────────────────────────────────────

export interface SequentialUploadProgress {
  /** 1-based index of the batch that just finished. */
  batch: number;
  batchTotal: number;
  /** Files confirmed uploaded so far, across all batches. */
  uploaded: number;
  /** Files handed to the runner in total. */
  total: number;
  /** The response body of the batch that just finished, when it succeeded. */
  response?: any;
}

export interface SequentialUploadOptions {
  /**
   * POST one batch. The runner awaits each call before starting the next —
   * that ordering IS the fix, so callers must not parallelize inside `send`.
   *
   * `carry` is the token threaded from an earlier batch (the playbook
   * `upload_id`); it is `undefined` on the first batch.
   */
  send: (batch: File[], ctx: SequentialSendContext) => Promise<any>;
  /** Pull a carry token out of a batch response (playbooks' `upload_id`). */
  readCarry?: (response: any) => string | undefined;
  onProgress?: (progress: SequentialUploadProgress) => void;
  bounds?: BatchBounds;
  /**
   * What a transport failure on ONE batch means for the rest of the run.
   *
   * `'stop'` (the default, and what every caller did before this existed) ends
   * the run and reports every remaining file as failed. Right when the batches
   * are dependent - a playbook's `upload_id` threads through them, so batch 4
   * without batch 3 is not a smaller playbook, it is a broken one.
   *
   * `'continue'` records the failed batch's files in `failed` and moves to the
   * next batch. Right when batches are INDEPENDENT, which is the tables case: a
   * table of 180 documents is 180 unrelated rows, and one batch failing is a
   * reason to be missing twenty of them, not a hundred and sixty.
   */
  onBatchError?: 'stop' | 'continue';
  /**
   * A stable per-batch identifier, for a backend that de-duplicates retries.
   *
   * Called once per batch and passed to `send` as `ctx.batchKey`, so the same
   * key goes up on the first attempt and on all its retries. Without one, a
   * retried `/documents` after a 500 that had in fact stored its rows creates
   * them twice.
   */
  batchKey?: (batch: File[], index: number) => string;
  /** Attempts per batch, retries included. Default `UPLOAD_RETRY_ATTEMPTS` (3). */
  attempts?: number;
  /**
   * Backoff before attempt N+1. Defaults to ~1s/2s/4s with full jitter.
   *
   * Injectable so tests can assert the retry BEHAVIOUR without sleeping
   * through nine real seconds of it.
   */
  retryDelayMs?: (attempt: number) => number;
  /**
   * Called when a batch attempt fails and will be retried, for logging. Never
   * called on the final attempt - that failure goes to `failed`/`error`.
   */
  onRetry?: (info: { index: number; attempt: number; delayMs: number; error: any }) => void;
  /**
   * Called ONCE per batch that finally failed, retries exhausted.
   *
   * The caller learns which files are definitely lost while the run is still
   * going, which is what lets tables keep `expected_total` honest instead of
   * asserting the number it hoped for and reconciling much later. Not called
   * for the all-rejected 400 - those files are `skipped`, not failed.
   */
  onBatchFailed?: (info: { index: number; files: File[]; error: any }) => void;
}

export interface SequentialSendContext {
  index: number;
  total: number;
  carry?: string;
  /** From `options.batchKey`, stable across this batch's retries. */
  batchKey?: string;
  /** 1-based; > 1 means this batch is being retried. */
  attempt: number;
}

export interface SequentialUploadResult {
  /** True when every batch was POSTed without a transport failure. */
  ok: boolean;
  /** Files handed to the runner. */
  attempted: number;
  /** Files the server confirmed it stored. */
  uploadedCount: number;
  /** Every file the server refused, aggregated across batches. */
  skipped: SkippedFile[];
  /** Names of files in batches that never landed, after a transport failure. */
  failed: string[];
  /** Each successful batch's response body, in order. */
  responses: any[];
  /** The last carry token seen (playbooks pass this on to /extract). */
  carry?: string;
  /** The error that stopped the run, when one did. */
  error?: any;
}

/**
 * Upload `files` in bounded batches, one request at a time.
 *
 * Never throws: a caller needs the partial result - which files landed, which
 * were refused, which never went - far more than it needs an exception. Check
 * `ok` / `skipped` / `failed`.
 */
export async function runSequentialUpload(
  files: File[],
  options: SequentialUploadOptions,
): Promise<SequentialUploadResult> {
  const {
    send, readCarry, onProgress, bounds,
    onBatchError = 'stop',
    batchKey,
    attempts = UPLOAD_RETRY_ATTEMPTS,
    retryDelayMs: delayFor = retryDelayMs,
    onRetry,
    onBatchFailed,
  } = options;

  const result: SequentialUploadResult = {
    ok: true,
    attempted: files.length,
    uploadedCount: 0,
    skipped: [],
    failed: [],
    responses: [],
  };
  if (!files.length) return result;

  const batches = buildUploadBatches(files, bounds);
  let carry: string | undefined;

  /**
   * One batch, with its retries.
   *
   * Rethrows whatever finally beat it, so the loop below decides what a failed
   * batch means for the run. The 400-all-rejected case is deliberately NOT
   * retried in here: it is a content verdict and reaches the loop first time.
   */
  const sendWithRetry = async (batch: File[], index: number): Promise<any> => {
    const key = batchKey?.(batch, index);
    let lastError: any;
    for (let attempt = 1; attempt <= Math.max(1, attempts); attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop -- retries are sequential by definition
        return await send(batch, { index, total: batches.length, carry, batchKey: key, attempt });
      } catch (error: any) {
        lastError = error;
        const lastAttempt = attempt >= Math.max(1, attempts);
        if (lastAttempt || isAllRejected(error) || !isRetryable(error)) throw error;
        const delayMs = delayFor(attempt);
        onRetry?.({ index, attempt, delayMs, error });
        // eslint-disable-next-line no-await-in-loop -- backoff
        await sleep(delayMs);
      }
    }
    throw lastError;
  };

  for (let index = 0; index < batches.length; index += 1) {
    const batch = batches[index];
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential batches ARE the fix
      const response = await sendWithRetry(batch, index);
      const payload = response?.data ?? response;

      const skipped = normalizeSkipped(payload);
      result.skipped.push(...skipped);
      result.uploadedCount += readUploadedCount(payload, batch.length, skipped.length);
      result.responses.push(payload);

      const nextCarry = readCarry?.(payload);
      if (nextCarry) carry = nextCarry;

      onProgress?.({
        batch: index + 1,
        batchTotal: batches.length,
        uploaded: result.uploadedCount,
        total: files.length,
        response: payload,
      });
    } catch (error: any) {
      if (isAllRejected(error)) {
        // Every file in THIS batch was refused on content grounds. That is an
        // answer, not an outage - record the reasons and keep going, so one
        // bad folder doesn't strand the documents queued behind it.
        result.skipped.push(...normalizeSkipped(error.response.data));
        onProgress?.({
          batch: index + 1,
          batchTotal: batches.length,
          uploaded: result.uploadedCount,
          total: files.length,
        });
        // eslint-disable-next-line no-continue
        continue;
      }

      result.ok = false;
      onBatchFailed?.({ index, files: batch, error });
      // The FIRST failure is the one worth reporting: later ones are usually
      // the same outage seen again, and overwriting leaves the user reading
      // whichever batch happened to be last.
      if (!result.error) result.error = error;

      if (onBatchError === 'continue') {
        // This batch's files never landed. The next batch might - 180 unrelated
        // documents should not all be lost because the twentieth one was.
        result.failed.push(...batch.map((f) => f.name));
        onProgress?.({
          batch: index + 1,
          batchTotal: batches.length,
          uploaded: result.uploadedCount,
          total: files.length,
        });
        // eslint-disable-next-line no-continue
        continue;
      }

      // Everything from this batch onward never went up; say so by name rather
      // than letting the count quietly disagree.
      for (let rest = index; rest < batches.length; rest += 1) {
        result.failed.push(...batches[rest].map((f) => f.name));
      }
      break;
    }
  }

  result.carry = carry;
  return result;
}

// ─── Reporting ───────────────────────────────────────────────────────────────

/**
 * One honest sentence about a finished run, or `null` when everything landed.
 *
 * Deliberately never claims success it can't see: after batching, a failed run
 * leaves a REAL entity holding SOME of its documents, and the user has to be
 * told which ones are missing so they can retry from that entity's documents
 * tab rather than wondering why the analysis looks thin.
 */
export function describeUploadResult(result: SequentialUploadResult): string | null {
  const parts: string[] = [];
  if (result.failed.length) {
    parts.push(
      result.failed.length === 1
        ? `${result.failed[0]} could not be uploaded.`
        : `${result.failed.length} files could not be uploaded: ${result.failed.slice(0, 5).join(', ')}${result.failed.length > 5 ? '…' : ''}`,
    );
  }
  if (result.skipped.length) {
    const withReasons = result.skipped
      .slice(0, 5)
      .map((s) => (s.reason ? `${s.file_name} (${s.reason})` : s.file_name))
      .join(', ');
    parts.push(
      `${result.skipped.length} file${result.skipped.length === 1 ? ' was' : 's were'} skipped: ${withReasons}${result.skipped.length > 5 ? '…' : ''}`,
    );
  }
  if (!parts.length) return null;
  parts.push('You can add them again from the documents tab.');
  return parts.join(' ');
}

/** True when a run left the entity with fewer documents than the user picked. */
export function isPartialUpload(result: SequentialUploadResult): boolean {
  return !result.ok || result.skipped.length > 0 || result.failed.length > 0;
}
